import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { getATLContextUrl } from '@atlaskit/atlassian-context/get-atl-context-url';
import { getUrlForDomainInContext } from '@atlaskit/atlassian-context/get-url-for-domain-in-context';
import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import { isolatedCloudDomain } from '@atlaskit/atlassian-context/isolated-cloud-domain';
import { getConfig } from '@atlassian/teams-common/context-config/config';

import teamCentralRestClient from '../clients/team-central-rest-client';

/**
 * @deprecated Use `TeamCentralHandlers` from `@atlassian/teams-app-internal-team-central` instead.
 * This will be removed in a future release.
 */
export interface TeamCentralHandlers {
	addProject?: () => void;
	getActiveWorkspaceId?: () => string;
}

type State = {
	isTeamCentral: boolean;
	// Whether the given cloudId has a team central workspace
	hasWorkspace: boolean;
	// Whether the org containing the given cloudId has a team central workspace
	orgHasWorkspace: boolean;
	// Hooks back to team central to perform specific actions
	teamCentralHandlers: TeamCentralHandlers;
	// the URL for team central
	teamCentralUrl?: string;
};
type StoreApi = StoreActionApi<State>;

const initialState: State = {
	isTeamCentral: false,
	hasWorkspace: false,
	orgHasWorkspace: false,
	teamCentralHandlers: {} as TeamCentralHandlers,
	teamCentralUrl: undefined,
};

const actions = {
	initTeamCentral:
		(appName: string, cloudId: string, teamCentralHandlers: TeamCentralHandlers = {}) =>
		({ setState }: StoreApi): void => {
			const isTeamCentral = appName === 'teamcentral-directory';

			let teamCentralUrlPromise: Promise<string | undefined>;
			if (getConfig().teamCentralDisabled === true) {
				teamCentralUrlPromise = Promise.resolve(undefined);
			} else {
				teamCentralUrlPromise = teamCentralRestClient
					.isGlobalExperienceWorkspaceForCloudId(cloudId)
					.then((isGlobalExperience) => {
						// Isolated cloud only functionality uses prod only environment to get an IC domain
						if (isIsolatedCloud()) {
							return isGlobalExperience
								? getUrlForDomainInContext('home', 'prod')
								: `https://team.${isolatedCloudDomain() ?? ''}`;
						}
						return isGlobalExperience ? getATLContextUrl('home') : getATLContextUrl('team');
					});
			}

			if (isTeamCentral) {
				// Assume site already has a TC workspace if directory is running in TC mode
				teamCentralUrlPromise.then((teamCentralUrl) => {
					setState({
						isTeamCentral,
						hasWorkspace: true,
						orgHasWorkspace: true,
						teamCentralHandlers,
						teamCentralUrl,
					});
				});
			} else {
				// Otherwise, fetch for workspace information
				const requests: [Promise<boolean>, Promise<boolean>, Promise<string | undefined>] = [
					teamCentralRestClient.hasWorkspaceForCloudId(cloudId),
					teamCentralRestClient.doesOrgContainAnyWorkspace(cloudId),
					teamCentralUrlPromise,
				];

				Promise.all(requests).then(([hasWorkspace, orgHasWorkspace, teamCentralUrl]) => {
					setState({
						isTeamCentral: false,
						hasWorkspace,
						orgHasWorkspace,
						teamCentralHandlers,
						teamCentralUrl,
					});
				});
			}
		},
	initTeamCentralWithValues:
		({
			appName,
			hasWorkspace,
			orgHasWorkspace,
			teamCentralHandlers = {},
			teamCentralUrl,
		}: {
			appName: string;
			hasWorkspace: boolean;
			orgHasWorkspace: boolean;
			teamCentralUrl: string;
			teamCentralHandlers: TeamCentralHandlers;
		}) =>
		({ setState }: StoreApi): void => {
			setState({
				isTeamCentral: appName === 'teamcentral-directory',
				hasWorkspace,
				orgHasWorkspace,
				teamCentralHandlers,
				teamCentralUrl,
			});
		},
};

type Actions = typeof actions;

/**
 * Manage states of searching People and Teams in search home page.
 */
const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'SearchHomePage',
});

/**
 * @deprecated Use `useTeamCentralStore` from `@atlassian/teams-app-internal-team-central` instead.
 * This will be removed in a future release.
 */
export const useTeamCentralStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			initTeamCentral: (
				appName: string,
				cloudId: string,
				teamCentralHandlers?: TeamCentralHandlers,
			) => ({ setState }: StoreApi) => void;
			initTeamCentralWithValues: ({
				appName,
				hasWorkspace,
				orgHasWorkspace,
				teamCentralHandlers,
				teamCentralUrl,
			}: {
				appName: string;
				hasWorkspace: boolean;
				orgHasWorkspace: boolean;
				teamCentralUrl: string;
				teamCentralHandlers: TeamCentralHandlers;
			}) => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(Store);
/**
 * @deprecated Use `useTeamCentralStoreApi` from `@atlassian/teams-app-internal-team-central` instead.
 * This will be removed in a future release.
 */
export const useTeamCentralStoreApi: HookFunction<
	State,
	BoundActions<
		State,
		{
			initTeamCentral: (
				appName: string,
				cloudId: string,
				teamCentralHandlers?: TeamCentralHandlers,
			) => ({ setState }: StoreApi) => void;
			initTeamCentralWithValues: ({
				appName,
				hasWorkspace,
				orgHasWorkspace,
				teamCentralHandlers,
				teamCentralUrl,
			}: {
				appName: string;
				hasWorkspace: boolean;
				orgHasWorkspace: boolean;
				teamCentralUrl: string;
				teamCentralHandlers: TeamCentralHandlers;
			}) => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(Store, { selector: null });
/**
 * @deprecated Use `useIsTeamCentral` from `@atlassian/teams-app-internal-team-central` instead.
 * This will be removed in a future release.
 */
export const useIsTeamCentral: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			initTeamCentral: (
				appName: string,
				cloudId: string,
				teamCentralHandlers?: TeamCentralHandlers,
			) => ({ setState }: StoreApi) => void;
			initTeamCentralWithValues: ({
				appName,
				hasWorkspace,
				orgHasWorkspace,
				teamCentralHandlers,
				teamCentralUrl,
			}: {
				appName: string;
				hasWorkspace: boolean;
				orgHasWorkspace: boolean;
				teamCentralUrl: string;
				teamCentralHandlers: TeamCentralHandlers;
			}) => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(Store, {
	selector: (s) => s.isTeamCentral,
});
/**
 * @deprecated Use `useTeamCentralPresent` from `@atlassian/teams-app-internal-team-central` instead.
 * This will be removed in a future release.
 */
export const useTeamCentralPresent: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			initTeamCentral: (
				appName: string,
				cloudId: string,
				teamCentralHandlers?: TeamCentralHandlers,
			) => ({ setState }: StoreApi) => void;
			initTeamCentralWithValues: ({
				appName,
				hasWorkspace,
				orgHasWorkspace,
				teamCentralHandlers,
				teamCentralUrl,
			}: {
				appName: string;
				hasWorkspace: boolean;
				orgHasWorkspace: boolean;
				teamCentralUrl: string;
				teamCentralHandlers: TeamCentralHandlers;
			}) => ({ setState }: StoreApi) => void;
		}
	>,
	void
> = createHook(Store, {
	selector: (s) => s.isTeamCentral || s.hasWorkspace || s.orgHasWorkspace,
});
